/**
 * Helpers shared by the Excel exports: line-shape detection and field access across cart, list and
 * quote lines, per-line price lookup, `$0` detection, weight formatting and column-width
 * measurement. Used by `./ExcelExport.tsx` and `./Products/ProductsExportUtils.tsx`.
 */
import { emDash, ShoppingListItem } from "@ryerson/frontend.common";
import { ShoppingCartItem, LineItem } from "@ryerson/frontend.e-commerce";
import { truncateNumber } from "@components/Helper/common";
import { formatNumber } from "@utils/NumberFormatUtil";
import { camelToPretty } from "../ProcessingDescription/ProcessingDescription";

/** Display values of a line's cut, keyed by cut field name, as the product export reads them. */
export interface CutProcessingDetails {
	cutShape?: string;
	processing?: string;
	outsideDiameter?: string;
	outsideTolerance?: string;
	insideDiameter?: string;
	insideTolerance?: string;
}

interface ExcelItems {
	itemNumber: string | number;
	extendedPrice: number;
	pricePerUom: string | number;
	weight: string | number;
	totalExtendedPrice?: number;
	itemSpecialCharges?: number;
}

/**
 * True for a quote line (`LineItem` from `@ryerson/frontend.e-commerce`), recognised by having both
 * `requestedQuantity` and `itemSkuNumber`; cart and shopping-list lines have neither.
 */
export function isLineItem(item: unknown): item is LineItem {
	const hasRequestedQuantity = (o: object): o is { quantity: unknown } =>
		"requestedQuantity" in o;
	const hasSkuNumber = (o: object): o is { skuNumber: unknown } => "itemSkuNumber" in o;
	if (
		item !== null &&
		typeof item == "object" &&
		hasRequestedQuantity(item) &&
		hasSkuNumber(item)
	) {
		return true;
	} else return false;
}

/**
 * Finds a line's prices in a pricing response. `rowIndex` is 0-based and pricing items carry a
 * 1-based `itemNumber`, so the line at index `i` is the pricing item numbered `i + 1`.
 *
 * When no pricing item matches, the price per UOM, extended price and weight are em dashes (which
 * the product export turns into "TBD"), `isZero` is false, and `itemSpecialCharges` and
 * `totalExtendedPrice` are 0. When one matches, `totalExtendedPrice` is 0 unless the line has item
 * special charges, and `isZero` is true when the price per UOM is exactly the number 0.
 */
export const getPricePerUomAndExtendedPriceOfItem = (rowIndex: number, items: ExcelItems[]) => {
	const itemPricingInfo = items?.find((item) => item.itemNumber === rowIndex + 1);

	if (itemPricingInfo) {
		let extendedPrice = Number(itemPricingInfo.extendedPrice);
		const itemSpecialCharges = itemPricingInfo.itemSpecialCharges
			? itemPricingInfo.itemSpecialCharges
			: 0;
		return {
			extendedPrice: Number(extendedPrice).toFixed(2),
			pricePerUom: itemPricingInfo.pricePerUom,
			isZero: itemPricingInfo?.pricePerUom === 0 ? true : false,
			totalWeight: itemPricingInfo?.weight,
			itemSpecialCharges: itemSpecialCharges,
			// if itemSpecialCharges is 0 we do not need to show totalExtendedPrice
			totalExtendedPrice:
				itemPricingInfo.totalExtendedPrice && itemSpecialCharges
					? itemPricingInfo.totalExtendedPrice
					: 0,
		};
	}

	return {
		extendedPrice: emDash,
		pricePerUom: emDash,
		isZero: false,
		totalWeight: emDash,
		itemSpecialCharges: 0,
		totalExtendedPrice: 0,
	};
};

/**
 * Pixel width of `text` in the page's body font, measured by appending a temporary `<span>` to
 * `document.body`. Browser only.
 */
export const computeTextWidth = (text: string): number => {
	const span = document.createElement("span");
	span.append(text);
	document.body.appendChild(span);
	const width = span.offsetWidth;
	document.body.removeChild(span);
	return width;
};

/**
 * True for a zero amount, whether a number, a numeric string or the formatted `"$0.00"`. The
 * product export shows "TBD" instead of any zero price or weight, under the price rule quoted in
 * the header of `./Products/ProductsExportUtils.tsx`.
 */
export const isZero = (value: string | number) => value === "$0.00" || parseFloat(`${value}`) === 0;

/** A weight truncated (not rounded) to two decimals and formatted with thousands separators. */
export const formatWeight = (totalWeight: string | number) => formatNumber(truncateNumber(Number(totalWeight), 2));

/**
 * Reads the fields the product export needs from a cart line, a shopping-list line or a quote line,
 * mapping the quote line's field names (`requestedQuantity`, `itemSkuNumber`, `cutProcessing`, ...)
 * to the cart's.
 */
export const getValues = (item: ShoppingCartItem | ShoppingListItem | LineItem) => {
	if (isLineItem(item)) {
		const {
			requestedQuantity: quantity,
			requestedQuantityUom: quantityUom,
			pricingUom,
			customerReferenceNumber: customerRefNumber,
			lineItemNotes: notes,
			domesticRequired,
			itemSkuNumber: ryersonSku,
			cutProcessing: processing,
		} = item;

		return {
			quantity,
			quantityUom,
			pricingUom,
			customerRefNumber,
			notes,
			domesticRequired,
			ryersonSku,
			processing,
		};
	} else {
		const {
			quantity,
			quantityUom,
			pricingUom,
			customerRefNumber,
			notes,
			domesticRequired,
			ryersonSku,
			processing,
		} = item as ShoppingCartItem | ShoppingListItem;

		return {
			quantity,
			quantityUom,
			pricingUom,
			customerRefNumber,
			notes,
			domesticRequired,
			ryersonSku,
			processing,
		};
	}
};

/**
 * The brand name written into the terms-of-sale note, from the build's `NEXT_PUBLIC_COMPANY`
 * (`"ryerson"` becomes "Ryerson", `"southernToolSteel"` becomes "Southern Tool Steel").
 */
export const displayCompanyName = camelToPretty(process.env.NEXT_PUBLIC_COMPANY || "Ryerson");